import { observer } from "mobx-react";
import React from "react";
import { Link } from "react-router-dom";
import { generalConfigurationStore } from "../Stores/GeneralConfigurationStore";
import { IModule, IGeneralModule, IOption } from "../Utils/Interface";
import utilsFunctions from "../Utils/ModuleHelpers";
import Accordion from "./Accordion";
import CheckBoxArea from "./CheckBoxArea";
import EmptyStateTile from "./EmptyStateTile";
import GeneralModuleAddOns from "./GeneralModuleAddOns";
import GeneralModuleTile from "./GeneralModuleTile";
import ModuleAddOns from "./ModuleAddOns";
import ModuleTile from "./ModuleTile";
import Selection from "./Selection";
import "./Sidebar.css";
import "./Checkbox.css";
import TextAreaField from "./TextAreaField";
import TextInputField from "./TextInputField";
import { first } from "lodash";
import { Options } from "../config";
import { ProductConfigurationStore } from "../Stores/ProductConfigurationStore";

type Props = {
	productConfigurationStore: ProductConfigurationStore;
};

type State = {
	accordionOpen: boolean;
	linkDisabled: boolean;
};

@observer
class Sidebar extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		// set initial state in constructor directly
		this.state = {
			accordionOpen: true,
			linkDisabled: true,
		};
	}

	renderModuleSteps() {
		const addonHeadline = generalConfigurationStore.configs?.generalModuleAddonHeadline + (generalConfigurationStore.configs?.generalOptional ? " (" + generalConfigurationStore.configs?.generalOptional + ")" : "");

		return this.props.productConfigurationStore.modules?.steps?.map((step, index) => {
			const getAnchor = utilsFunctions.getAnchor(step.stepHeadline);

			return (
				<Accordion key={`${index}-step`} headline={step.stepHeadline} anchor={getAnchor}>
					<div className="step-body">
						{generalConfigurationStore.configs?.modulesStepsHeadline &&
							<div className="step-body-headline">
								<h2>{generalConfigurationStore.configs?.modulesStepsHeadline}</h2>
							</div>
						}

						{step.modules?.map((module, index) => {
							return (
								<ModuleTile
									key={`${module.id}-${index}-step`}
									module={module}
									step={step}
									canSelectModule={(module) => {
										return this.props.productConfigurationStore.canSelectModule(module, step);
									}}
									isSelected={(module) => {
										return this.props.productConfigurationStore.isModuleSelected(module, step);
									}}
									onChange={(module) => {
										if (this.props.productConfigurationStore.isModuleSelected(module, step) && !step.requiresSelection) {
											this.props.productConfigurationStore.deselectModule(module, step);
										} else if (this.props.productConfigurationStore.canSelectModule(module, step).valid) {
											this.props.productConfigurationStore.deselectAllModulesOfStep(step); // single selection
											this.props.productConfigurationStore.selectModule(module, step);
										}
									}}
								/>
							);
						})}
						{this.props.productConfigurationStore.selection.modules[step.stepId]?.filter((module) => (module.addonModules?.length || 0) > 0)
							.length > 0 && (
								<div className="selection-wrapper">
									<div className="selection-headline-wrapper mb-2 pb-4">
										<h4 className="selection-headline">
											{addonHeadline}
										</h4>
									</div>
									{this.props.productConfigurationStore.selection.modules[step.stepId].map((module, moduleKey) => {
										return (
											<div
												className={module.addonModuleDisplayType === "tile" ? "tiles-wrapper" : ""}
												key={`${moduleKey}-addOns`}
											>
												{module.addonModules?.map((addOn: IModule, index) => {
													return (
														<ModuleAddOns
															key={`${index}-${addOn.id}-addOn`}
															addOn={addOn}
															onChange={(addOn) => {
																this.props.productConfigurationStore.selectModule(addOn, step);
															}}
															isSelected={(addOn) => {
																return this.props.productConfigurationStore.isModuleSelected(addOn, step);
															}}
															canSelectModule={(addOn) => {
																return this.props.productConfigurationStore.canSelectModule(addOn);
															}}
															displayType={module.addonModuleDisplayType}
														/>
													);
												})}
											</div>
										);
									})}
								</div>
							)}
					</div>
				</Accordion>
			);
		});
	}

	// requires a step with IGeneralModules
	renderGeneralModules() {
		const generalStep = this.props.productConfigurationStore.modules?.generalStep;
		const getAnchor = utilsFunctions.getAnchor(generalStep?.stepHeadline);
		const addonHeadline = generalConfigurationStore.configs?.generalModuleAddonHeadline + (generalConfigurationStore.configs?.generalOptional ? " (" + generalConfigurationStore.configs?.generalOptional + ")" : "");

		return (
			<Accordion headline={generalStep?.stepHeadline} anchor={getAnchor}>
				<div className="step-body">
					{generalStep?.moduleTypes?.map((moduleType, i) => {
						return (
							<div className="general-step-body" key={`${i}-moduleType`}>
								<div className="step-body-headline" id={utilsFunctions.getAnchor(moduleType.termName)}>
									<h2>{moduleType.termName}</h2>
								</div>
								{moduleType.modules &&
									moduleType.modules?.map((module) => {
										return (
											<GeneralModuleTile
												key={`${module.id}-gmodule`}
												generalModule={module}
												moduleType={moduleType}
												onChange={(module) => {
													if (this.props.productConfigurationStore.isGeneralModuleSelected(module, moduleType)) {
														this.props.productConfigurationStore.deselectGeneralModule(module, moduleType);
													} else {
														this.props.productConfigurationStore.deselectAllGeneralModulesOfModuleType(moduleType); // single selection
														this.props.productConfigurationStore.selectGeneralModule(module, moduleType);
													}
												}}
												isSelected={(module) => {
													return this.props.productConfigurationStore.isGeneralModuleSelected(module, moduleType);
												}}
												canSelectModule={(module) => {
													const canSelectModule = this.props.productConfigurationStore.canSelectGeneralModule(module);
													return canSelectModule;
												}}
											></GeneralModuleTile>
										);
									})}
								<EmptyStateTile
									emptyText={moduleType.emptyStateText || ""}
									isSelected={this.props.productConfigurationStore.isModuleTypeSelected(moduleType)}
									onChange={() => this.props.productConfigurationStore.clearModuleTypeSelection(moduleType)}
								></EmptyStateTile>

								{this.props.productConfigurationStore.selection.generalModules[moduleType.termId]?.filter(
									(module) => (module.addonModules?.length || 0) > 0
								).length > 0 && (
										<div className="selection-wrapper">
											<div className="selection-headline-wrapper mb-2 pb-4">
												<h4 className="selection-headline">
													{addonHeadline}
												</h4>
											</div>
											{this.props.productConfigurationStore.selection.generalModules[moduleType.termId].map((module, generalModuleKey) => {
												return (
													<div
														className={module.addonModuleDisplayType === "tile" ? "tiles-wrapper" : ""}
														key={`${generalModuleKey}-generalModuleAddOns`}
													>
														{module.addonModules?.map((addOn: IGeneralModule, index) => {
															return (
																<GeneralModuleAddOns
																	key={`${index}-${addOn.id}-addOn`}
																	addOn={addOn}
																	onChange={(addOn) => {
																		this.props.productConfigurationStore.selectGeneralModule(addOn, moduleType);
																	}}
																	displayType={module.addonModuleDisplayType}
																	isSelected={(addOn) => {
																		return this.props.productConfigurationStore.isGeneralModuleSelected(addOn, moduleType);
																	}}
																	canSelectModule={(module) => {
																		const canSelectModule = this.props.productConfigurationStore.canSelectGeneralModule(module);
																		return canSelectModule;
																	}}
																/>
															);
														})}
													</div>
												)
											})}
										</div>
									)}
							</div>
						);
					})}
				</div>
			</Accordion>
		);
	}

	renderModuleDetails() {
		const globalConfigs = generalConfigurationStore.configs;
		const getAnchor = utilsFunctions.getAnchor(globalConfigs?.detailsStepHeadline);

		return (
			<Accordion headline={globalConfigs?.detailsStepHeadline} anchor={getAnchor}>
				<div className="step-body details">
					{this.props.productConfigurationStore.modules?.globalModuleOptions?.coulterWidths &&
						<Selection
							headline={globalConfigs?.detailsStepSubheadlineCoulterWidths || ""}
							options={this.props.productConfigurationStore.modules?.globalModuleOptions?.coulterWidths}
							popoverDescription={this.props.productConfigurationStore.modules?.globalModuleOptions?.coulterWidthsDescription}
							onChange={(option) => {
								console.log("select option", option);
								this.props.productConfigurationStore.selectOptionWithId(option, Options.coulterWidths, true);
							}}
							showEmptyOption
							value={first(this.props.productConfigurationStore.selection?.options[Options.coulterWidths])}
							canSelectOption={(option) => {
								return this.props.productConfigurationStore.canSelectCoulterWidthOption(option).valid;
							}}
						></Selection>
					}
					<CheckBoxArea
						headline={globalConfigs?.detailsStepSubheadlineRowWidths}
						options={this.props.productConfigurationStore.modules?.globalModuleOptions?.rowWidths || []}
						selectionId={Options.rowWidths}
						selectedOptions={this.props.productConfigurationStore.selection?.options[Options.rowWidths] || []}
						popoverDescription={this.props.productConfigurationStore.modules?.globalModuleOptions?.rowWidthsDescription}
						onChange={(option) => {
							this.props.productConfigurationStore.selectOptionWithId(option, Options.rowWidths, false);
						}}
						canSelectOption={(option) => {
							return this.props.productConfigurationStore.canSelectRowWidthOption(option);
						}}
					></CheckBoxArea>

					<CheckBoxArea
						headline={globalConfigs?.detailsStepSubheadlinePlantation}
						options={this.props.productConfigurationStore.modules?.globalModuleOptions?.planedPlantation || []}
						selectedOptions={this.props.productConfigurationStore.selection?.options[Options.plannedPlantations] || []}
						selectionId={Options.plannedPlantations}
						onChange={(option) => {
							this.props.productConfigurationStore.selectOptionWithId(option, Options.plannedPlantations, false);
						}}
					></CheckBoxArea>

					<div className="selection-wrapper">
						<h4 className="selection-headline no-border pb-4">
							{generalConfigurationStore.configs?.detailsStepSubheadlineTrackWidth}
							{" (" + generalConfigurationStore.configs?.generalOptional + ")"}
						</h4>
						<TextInputField
							placeholder={generalConfigurationStore.configs?.detailsStepPlaceholderTrackWidth}
							textId={Options.trackWidth}
						></TextInputField>
					</div>
				</div>
			</Accordion>
		);
	}

	renderCustomerInformation() {
		const globalConfigs = generalConfigurationStore.configs;
		const getAnchor = utilsFunctions.getAnchor(globalConfigs?.cultivationTypeHeadline);

		return (
			<div className="cultivation-type-step" id={getAnchor}>
				<div className="step-header">
					<div className="bar-headline">{generalConfigurationStore.configs?.cultivationTypeSubheadlineNote}</div>
				</div>
				<div className="step-body py-5">
					<TextAreaField
						placeholder={generalConfigurationStore.configs?.cultivationTypePlaceholderNote}
						rows={7}
						cols={50}
						textId={Options.customerAnnotation}
					></TextAreaField>
				</div>
			</div>
		);
	}

	renderDeliverType() {
		const anchor = utilsFunctions.getAnchor(this.props.productConfigurationStore.modules?.deliverType?.deliverTypesHeadline || "");
		const currentOption: IOption = {
			name: this.props.productConfigurationStore.selection?.deliverType?.name || "",
			id: this.props.productConfigurationStore.selection?.deliverType?.id || "",
		};

		return (
			<div className="deliver-type-step" id={anchor}>
				<Accordion headline={this.props.productConfigurationStore.modules?.deliverType?.deliverTypesHeadline} anchor={anchor}>
					<div className="step-body">
						<Selection
							headline={this.props.productConfigurationStore.modules?.deliverType?.deliverTypesHeadline || ""}
							options={this.props.productConfigurationStore.modules?.deliverType?.deliverTypes?.map((deliverType) => {
								const option: IOption = {
									name: deliverType.name,
									id: deliverType.id,
								};
								return option;
							})}
							showEmptyOption={false}
							value={currentOption}
							onChange={(option: IOption) => {
								const deliverType = first(
									this.props.productConfigurationStore.modules?.deliverType?.deliverTypes?.filter((type) => {
										return option.id === type.id;
									})
								);
								if (deliverType) {
									this.props.productConfigurationStore.selectDeliverType(deliverType);
								}
							}}
						></Selection>
					</div>
				</Accordion>
			</div>
		);
	}

	render() {
		return (
			<div className="sidebar">
				{this.props.productConfigurationStore.modules?.deliverType?.deliverTypes &&
					this.renderDeliverType()
				}

				{this.props.productConfigurationStore.modules?.steps &&
					this.renderModuleSteps()
				}

				{this.props.productConfigurationStore.modules?.generalStep?.moduleTypes &&
					this.renderGeneralModules()
				}

				{this.props.productConfigurationStore.modules?.globalModuleOptions &&
					this.renderModuleDetails()
				}

				{this.renderCustomerInformation()}

				<Link className={`btn btn-primary`} to={"/summary"}>
					{generalConfigurationStore.configs?.generalSubmitEquipmentSelection}
				</Link>
			</div>
		);
	}
}

export default Sidebar;
